/* eslint-disable no-param-reassign */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { API_URL } from 'config/env';
import { notifyApiError } from 'utils/apiErrorHandler';
import storage from 'utils/storage';

type HeaderSetter = {
  set: (name: string, value: string) => void;
};

const hasHeaderSetter = (value: unknown): value is HeaderSetter =>
  typeof value === 'object' &&
  value !== null &&
  'set' in value &&
  typeof (value as { set?: unknown }).set === 'function';

const authRequestInterceptor = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const token = storage.getToken();

  if (token) {
    if (hasHeaderSetter(config.headers)) {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      const currentHeaders = (config.headers ?? {}) as Record<string, string>;
      const nextHeaders: Record<string, string> = {
        ...currentHeaders,
        Authorization: `Bearer ${token}`,
      };
      config.headers = nextHeaders as InternalAxiosRequestConfig['headers'];
    }
  }

  return config;
};

export const customAxios: AxiosInstance = axios.create({
  baseURL: API_URL,
});

customAxios.interceptors.request.use(authRequestInterceptor);

customAxios.interceptors.response.use(
  (res) => res,
  (err: { response?: { status?: number; data?: { message?: unknown } } }) => {
    const status = err.response?.status;
    const is5xx = status != null && status >= 500 && status < 600;
    if (is5xx) {
      const rawMessage = err.response?.data?.message;
      let message = 'Something went wrong.';
      if (typeof rawMessage === 'string') message = rawMessage;
      else if (status === 502) message = 'Server unavailable. Try again.';
      notifyApiError(message);
    }
    return Promise.reject(err);
  }
);
